import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { journeyAPI } from '../services/api';
import { getAppDate, isJourneyMoreThanTwoMinutesPast } from '../utils/time';

const CreateJourney = ({ onJourneyCreated }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    destination: '',
    return_date: '',
    return_time: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    additional_info: ''
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.destination.trim()) {
      newErrors.destination = 'Destination is required';
    }
    
    if (!formData.return_date) {
      newErrors.return_date = 'Return date is required';
    } else {
      if (formData.return_date < getAppDate()) {
        newErrors.return_date = 'Return date must be today or in the future';
      }
    }
    
    if (!formData.return_time) {
      newErrors.return_time = 'Return time is required';
    } else if (formData.return_date) {
      if (isJourneyMoreThanTwoMinutesPast(formData.return_date, formData.return_time)) {
        newErrors.return_time = 'Return date and time cannot be more than 2 minutes in the past';
      }
    }
    
    if (!formData.contact_name.trim()) {
      newErrors.contact_name = 'Emergency contact name is required';
    }
    
    // Email validation (if provided)
    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid email address';
    }
    
    // Phone validation (basic, if provided)
    if (formData.contact_phone && !/^[\+]?[1-9][\d\s\-\(\)]{7,}$/.test(formData.contact_phone.replace(/\s/g, ''))) {
      newErrors.contact_phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    
    try {
      const response = await journeyAPI.createJourney(formData);
      
      onJourneyCreated(response.data.journey);
      toast.success('Journey alert registered successfully!');
      
      // Reset form
      setFormData({
        destination: '',
        return_date: '',
        return_time: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        additional_info: ''
      });
      
      navigate('/');
      
    } catch (error) {
      console.error('Error creating journey:', error);
      toast.error(error.message || 'Failed to register journey');
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    return getAppDate();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Register New Journey</h1>
        <p className="mt-2 text-gray-600">
          Set up safety monitoring for your hiking trip
        </p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Destination */}
          <div>
            <label htmlFor="destination" className="block text-sm font-medium text-gray-700 mb-2">
              Hiking Destination *
            </label>
            <input
              type="text"
              id="destination"
              name="destination"
              value={formData.destination}
              onChange={handleChange}
              placeholder="e.g., Blue Mountains, Wilson's Promontory"
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.destination ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.destination && (
              <p className="mt-1 text-sm text-red-600">{errors.destination}</p>
            )}
          </div>

          {/* Return Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="return_date" className="block text-sm font-medium text-gray-700 mb-2">
                Expected Return Date *
              </label>
              <input
                type="date"
                id="return_date"
                name="return_date"
                value={formData.return_date}
                onChange={handleChange}
                min={getMinDate()}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.return_date ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.return_date && (
                <p className="mt-1 text-sm text-red-600">{errors.return_date}</p>
              )}
            </div>

            <div>
              <label htmlFor="return_time" className="block text-sm font-medium text-gray-700 mb-2">
                Expected Return Time *
              </label>
              <input
                type="time"
                id="return_time"
                name="return_time"
                value={formData.return_time}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.return_time ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.return_time && (
                <p className="mt-1 text-sm text-red-600">{errors.return_time}</p>
              )}
            </div>
          </div>

          {/* Emergency Contact Details */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h3>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="contact_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Person in Need of Help *
                </label>
                <input
                  type="text"
                  id="contact_name"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  defaultValue={'Ian and Nilu'}
                  placeholder="Full name of emergency contact"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.contact_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.contact_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.contact_name}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="contact_phone"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="+61 4XX XXX XXX"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      errors.contact_phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.contact_phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.contact_phone}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="contact_email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="contact@example.com"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      errors.contact_email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.contact_email && (
                    <p className="mt-1 text-sm text-red-600">{errors.contact_email}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <label htmlFor="additional_info" className="block text-sm font-medium text-gray-700 mb-2">
              Additional Information
            </label>
            <textarea
              id="additional_info"
              name="additional_info"
              value={formData.additional_info}
              onChange={handleChange}
              rows={3}
              placeholder="Any additional details about your trip, route, or special instructions..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex space-x-4 pt-6">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Registering...
                </div>
              ) : (
                '🚨 Register Safety Alert'
              )}
            </button>
            
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-start">
            <span className="text-blue-600 mr-2">ℹ️</span>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>We'll monitor your expected return time</li>
                <li>If you don't check in, we'll alert your emergency contact via PagerDuty</li>
                <li>You can complete or cancel this alert anytime before the deadline</li>
                <li>Keep your phone with backup power for when you return to check in</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateJourney;
